import { getItem, setItem, deleteItem } from './storage';

const PENDING_PREFIX = 'mls:pending:';

export const makePendingQueueKey = (userId) => {
  if (!userId) return null;
  return `${PENDING_PREFIX}${userId}`;
};

const isObject = (value) => value && typeof value === 'object';

const normalizeEntry = (entry, fallbackKey) => {
  if (!isObject(entry)) return null;
  const key = entry.queueKey || entry.queue_key || fallbackKey;
  if (!key) return null;
  return {
    ...entry,
    queueKey: String(key),
    pendingRecovery: entry.pendingRecovery !== false,
  };
};

export const hydratePendingQueue = (payload) => {
  const map = new Map();
  if (!isObject(payload)) {
    return map;
  }

  Object.entries(payload).forEach(([groupId, rawEntries]) => {
    if (!groupId) return;
    const queue = new Map();
    if (Array.isArray(rawEntries)) {
      rawEntries.forEach((entry, index) => {
        const normalized = normalizeEntry(entry, `idx:${index}`);
        if (!normalized) return;
        queue.set(normalized.queueKey, normalized);
      });
    }
    if (queue.size > 0) {
      map.set(groupId, queue);
    }
  });

  return map;
};

export const serializePendingQueue = (queueMap) => {
  if (!(queueMap instanceof Map) || queueMap.size === 0) {
    return {};
  }
  const payload = {};
  queueMap.forEach((queue, groupId) => {
    if (!groupId || !(queue instanceof Map) || queue.size === 0) return;
    const entries = [];
    queue.forEach((value, key) => {
      if (!isObject(value)) return;
      const normalized = normalizeEntry(value, key);
      if (!normalized) return;
      const serialized = {
        ...normalized,
        queueKey: normalized.queueKey,
      };
      entries.push(serialized);
    });
    if (entries.length > 0) {
      entries.sort((a, b) => {
        const timeA = a.enqueuedAt == null ? 0 : Number(a.enqueuedAt);
        const timeB = b.enqueuedAt == null ? 0 : Number(b.enqueuedAt);
        return timeA - timeB;
      });
      payload[groupId] = entries;
    }
  });
  return payload;
};

export const loadPendingQueueFromStorage = async (storageKey) => {
  if (!storageKey) return new Map();
  try {
    const stored = await getItem(storageKey);
    return hydratePendingQueue(stored || {});
  } catch (error) {
    console.warn('[MLS] failed to load pending queue', error);
    return new Map();
  }
};

export const persistPendingQueueToStorage = async (storageKey, queueMap) => {
  if (!storageKey) return;
  const payload = serializePendingQueue(queueMap);
  const hasEntries = Object.keys(payload).length > 0;
  try {
    if (!hasEntries) {
      await deleteItem(storageKey);
    } else {
      await setItem(storageKey, payload);
    }
  } catch (error) {
    console.warn('[MLS] failed to persist pending queue', error);
  }
};
